/*
 * Client "Iron Imbalance Consistent with:" boxes for the Iron Insights panel
 * (iron-status-insights.tsx).
 *
 * HAND-AUTHORED, not synced (2026-09-20). The patterns sheet has no "Iron"
 * tab yet: every bullet below is an EXISTING root-cause label lifted word for
 * word from the "Root Causes w Iron" rules that already fire inside the RBC
 * section (RootCause.ts, rbc-patterns-tab.csv rules 2.10-2.38), so the panel
 * says nothing the RBC panel does not already say to the same client. When an
 * Iron tab is authored, this file becomes the target of a
 * sync-iron-pattern-insights.js in the thyroid/adrenal mold and gets the
 * `.generated` suffix; the resolver in IronAnalysis.ts is unaffected.
 *
 * `logic` is the pattern's marker signature (the sheet's Logic column), in
 * display order, for the ▲/▼ row under each box. The row shows only the
 * markers actually off in this client — a signature marker that reads
 * optimal or was not run is left out, the way the Thyroid boxes work.
 * Cross-section markers (WBC, absolute neutrophils) sit last, so the iron
 * quartet always reads first.
 */

export type IronLogicMarker = { label: string; key: string; dirs: Array<'high' | 'low'> };

export interface IronPatternInsight {
  patternNumber: string;
  title: string;
  bullets: string[];
  consistentWith: boolean;
  logic: IronLogicMarker[];
}

const IRON = (dir: 'high' | 'low'): IronLogicMarker => ({ label: 'Iron', key: 'iron', dirs: [dir] });
const TIBC = (dir: 'high' | 'low'): IronLogicMarker => ({ label: 'TIBC', key: 'tibc', dirs: [dir] });
const SAT = (dir: 'high' | 'low'): IronLogicMarker => ({ label: '% Sat', key: 'saturation', dirs: [dir] });
const FERRITIN = (dir: 'high' | 'low'): IronLogicMarker => ({ label: 'Ferritin', key: 'ferritin', dirs: [dir] });
const WBC = (dir: 'high' | 'low'): IronLogicMarker => ({ label: 'WBC', key: 'wbc', dirs: [dir] });
const NEUTROPHILS_LOW: IronLogicMarker = { label: 'Neutrophils (Abs)', key: 'neutrophilsAbs', dirs: ['low'] };

const TITLE = 'Iron Imbalance';

// Existing root-cause labels (RootCause.ts), quoted exactly.
const L = {
  IRON_INSUFFICIENCY: 'Iron Insufficiency',
  IRON_STORAGE_INSUFFICIENCY: 'Iron Storage Insufficiency',
  UNDETERMINED_SERUM_IRON: 'Undetermined Serum Iron Status (Need % Saturation and TIBC)',
  IRON_INSUFFICIENCY_COPPER: 'Iron Insufficiency Possibly Caused by Copper Insufficiency',
  IRON_STORAGE_INSUFFICIENCY_COPPER: 'Iron Storage Insufficiency Possibly Caused by Copper Insufficiency',
  SERUM_IRON_INFLAMMATION:
    'Serum Iron Insufficiency Possibly Caused by Chronic Inflammation and/or Oxidative Stress',
  SERUM_IRON_CHRONIC_INFECTION: 'Serum Iron Insufficiency Possibly Caused by Chronic Infection',
  SERUM_IRON_ACUTE_INFECTION: 'Serum Iron Insufficiency Possibly Caused by Acute Infection',
  SERUM_IRON_IMMUNE_ABNORMALITY: 'Serum Iron Insufficiency Possibly Caused by Immune System Abnormality',
  CHRONIC_INFLAMMATION: 'Chronic Inflammation',
  OXIDATIVE_STRESS: 'Oxidative Stress',
  // Sheet rule 2.8 (categoryId 207): authored, never coded.
  SERUM_IRON_EXCESS: 'Chronic Inflammation and/or Oxidative Stress Possibly Caused by Serum Iron Excess',
};

// The two low-ferritin signatures share the iron quartet minus % saturation
// (which reads optimal in Storage Insufficiency and is absent in the
// undetermined variant, so it never draws an arrow there).
const INSUFFICIENCY_LOGIC = [IRON('low'), TIBC('high'), SAT('low'), FERRITIN('low')];
const STORAGE_LOGIC = [IRON('low'), TIBC('high'), FERRITIN('low')];
const UNDETERMINED_LOGIC = [IRON('low'), FERRITIN('low')];
const SERUM_INSUFFICIENCY_LOGIC = [IRON('low'), TIBC('low'), SAT('low'), FERRITIN('high')];

export const IRON_PATTERN_INSIGHTS: Record<number, IronPatternInsight> = {
  // ── Group 1: ferritin below optimal (sheet rules 2.10-2.21, 2.25-2.27, 2.33-2.37) ──
  97100: { patternNumber: '1.1', title: TITLE, bullets: [L.IRON_INSUFFICIENCY], consistentWith: true, logic: INSUFFICIENCY_LOGIC },
  97110: { patternNumber: '1.2', title: TITLE, bullets: [L.IRON_STORAGE_INSUFFICIENCY], consistentWith: true, logic: STORAGE_LOGIC },
  97120: { patternNumber: '1.3', title: TITLE, bullets: [L.IRON_STORAGE_INSUFFICIENCY, L.UNDETERMINED_SERUM_IRON], consistentWith: true, logic: UNDETERMINED_LOGIC },
  97130: { patternNumber: '1.4', title: TITLE, bullets: [L.IRON_INSUFFICIENCY_COPPER], consistentWith: true, logic: [...INSUFFICIENCY_LOGIC, NEUTROPHILS_LOW] },
  97140: { patternNumber: '1.5', title: TITLE, bullets: [L.IRON_STORAGE_INSUFFICIENCY_COPPER], consistentWith: true, logic: [...STORAGE_LOGIC, NEUTROPHILS_LOW] },
  97150: { patternNumber: '1.6', title: TITLE, bullets: [L.IRON_STORAGE_INSUFFICIENCY_COPPER, L.UNDETERMINED_SERUM_IRON], consistentWith: true, logic: [...UNDETERMINED_LOGIC, NEUTROPHILS_LOW] },

  // ── Group 2: % saturation below optimal with ferritin held (sheet rules 2.29-2.31, 2.38) ──
  97200: { patternNumber: '2.1', title: TITLE, bullets: [L.SERUM_IRON_INFLAMMATION], consistentWith: true, logic: SERUM_INSUFFICIENCY_LOGIC },
  97210: { patternNumber: '2.2', title: TITLE, bullets: [L.SERUM_IRON_INFLAMMATION, L.SERUM_IRON_CHRONIC_INFECTION], consistentWith: true, logic: [...SERUM_INSUFFICIENCY_LOGIC, WBC('low')] },
  97220: { patternNumber: '2.3', title: TITLE, bullets: [L.SERUM_IRON_INFLAMMATION, L.SERUM_IRON_ACUTE_INFECTION], consistentWith: true, logic: [...SERUM_INSUFFICIENCY_LOGIC, WBC('high')] },
  97230: { patternNumber: '2.4', title: TITLE, bullets: [L.SERUM_IRON_INFLAMMATION, L.SERUM_IRON_IMMUNE_ABNORMALITY], consistentWith: true, logic: SERUM_INSUFFICIENCY_LOGIC },

  // ── Group 3: ferritin or % saturation above optimal (sheet rules 2.1/2.28, 2.8) ──
  97300: { patternNumber: '3.1', title: TITLE, bullets: [L.CHRONIC_INFLAMMATION, L.OXIDATIVE_STRESS], consistentWith: true, logic: [IRON('low'), TIBC('low'), FERRITIN('high')] },
  97310: { patternNumber: '3.2', title: TITLE, bullets: [L.SERUM_IRON_EXCESS], consistentWith: true, logic: [IRON('high'), TIBC('low'), SAT('high'), FERRITIN('high')] },
};

export function getIronPatternInsight(categoryId: number): IronPatternInsight | undefined {
  return IRON_PATTERN_INSIGHTS[categoryId];
}
